import { useState, useEffect, useCallback } from "react";
import { ArrowLeft, Bell, Check, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { BottomNav } from "./bottom-nav";
import * as authClient from "@/app/api/auth-client";

export interface Notification {
  id: string;
  title: string;
  message: string;
  timestamp: string;
  isRead: boolean;
  type: "message" | "alert" | "sale" | "system";
  /** Raw type from API (e.g. swap_request_checkout) for copy/CTA. */
  apiType?: string;
  /** When set, clicking the notification navigates to this path (e.g. /offers/received?highlight=id). */
  link?: string;
}

/** Map BFF notification type to UI type for getIconColor. */
function apiTypeToUiType(apiType: string): Notification["type"] {
  switch (apiType) {
    case "chat":
      return "message";
    case "offer_sent":
    case "offer_received":
      return "alert";
    case "sale":
    case "purchase":
    case "swap_request_checkout":
      return "sale";
    case "favorite":
    case "promo":
    case "system":
    default:
      return "system";
  }
}

function formatRelative(createdAt: string): string {
  try {
    const date = new Date(createdAt);
    if (Number.isNaN(date.getTime())) return createdAt;
    return formatDistanceToNow(date, { addSuffix: true, locale: ptBR });
  } catch {
    return createdAt;
  }
}

function mapApiItemToNotification(item: authClient.NotificationItemResponse): Notification {
  return {
    id: item.id,
    title: item.title,
    message: item.message,
    timestamp: formatRelative(item.created_at),
    isRead: !!item.read_at,
    type: apiTypeToUiType(item.type),
    apiType: item.type?.trim() || undefined,
    link: item.link?.trim() || undefined,
  };
}

interface NotificationsPageProps {
  onBack: () => void;
  activeTab: "home" | "favorites";
  onTabChange: (tab: string) => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
  /** Called when list is loaded or updated so the app can update the bell badge. */
  onUnreadCountChange?: (count: number) => void;
  /** When the user taps a notification that has a link (e.g. offer), navigate to that path. */
  onNavigateToLink?: (link: string) => void;
}

export function NotificationsPage({
  onBack,
  activeTab,
  onTabChange,
  favoritesCount,
  onMenuClick,
  onPostAdClick,
  postAdBlockedByUnverifiedEmail = false,
  onUnreadCountChange,
  onNavigateToLink,
}: NotificationsPageProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    if (!authClient.getAccessToken()) {
      setLoading(false);
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await authClient.getNotifications({ limit: 50 });
      setNotifications(data.items.map(mapApiItemToNotification));
      setUnreadCount(data.unread_count);
      onUnreadCountChange?.(data.unread_count);
    } catch (e) {
      setError((e as Error).message ?? "Erro ao carregar notificações");
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [onUnreadCountChange]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleMarkAsRead = useCallback(
    async (id: string) => {
      try {
        await authClient.markNotificationsRead({ mark_read: [id] });
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
        );
        setUnreadCount((c) => Math.max(0, c - 1));
        onUnreadCountChange?.(Math.max(0, unreadCount - 1));
      } catch {
        // Optimistic revert or leave as-is
        await fetchList();
      }
    },
    [onUnreadCountChange, unreadCount, fetchList]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      const wasUnread = notifications.find((n) => n.id === id)?.isRead === false;
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      if (wasUnread) {
        setUnreadCount((c) => Math.max(0, c - 1));
        onUnreadCountChange?.(Math.max(0, unreadCount - 1));
      }
      try {
        await authClient.deleteNotification(id);
      } catch {
        await fetchList();
      }
    },
    [notifications, unreadCount, onUnreadCountChange, fetchList]
  );

  const getIconColor = (type: Notification["type"]) => {
    switch (type) {
      case "message":
        return "bg-blue-500";
      case "alert":
        return "bg-orange-500";
      case "sale":
        return "bg-green-500";
      case "system":
        return "bg-purple-500";
      default:
        return "bg-gray-500";
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl text-white">Notificações</h1>
              <p className="text-sm text-white/80">
                {unreadCount > 0
                  ? `${unreadCount} ${unreadCount === 1 ? "nova" : "novas"}`
                  : "Todas lidas"}
              </p>
            </div>
            <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
              <Bell className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto">
        {loading ? (
          <div className="py-16 px-4 text-center text-muted-foreground">
            Carregando…
          </div>
        ) : error ? (
          <div className="py-16 px-4 text-center">
            <p className="text-destructive mb-2">{error}</p>
            <button
              type="button"
              onClick={() => fetchList()}
              className="text-sm text-primary hover:underline"
            >
              Tentar novamente
            </button>
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-16 px-4">
            <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
              <Bell className="w-10 h-10 text-secondary-foreground" />
            </div>
            <h3 className="mb-2">Nenhuma notificação</h3>
            <p className="text-muted-foreground">
              Você está em dia! Nenhuma notificação no momento.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkAsRead={handleMarkAsRead}
                onDelete={handleDelete}
                onNavigateToLink={onNavigateToLink}
                iconColor={getIconColor(notification.type)}
              />
            ))}
          </div>
        )}
      </div>

      <BottomNav
        activeTab={activeTab}
        onTabChange={onTabChange}
        favoritesCount={favoritesCount}
        onMenuClick={onMenuClick}
        onPostAdClick={onPostAdClick}
        postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
      />
    </div>
  );
}

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
  onNavigateToLink?: (link: string) => void;
  iconColor: string;
}

function NotificationItem({
  notification,
  onMarkAsRead,
  onDelete,
  onNavigateToLink,
  iconColor,
}: NotificationItemProps) {
  const handleRowClick = () => {
    if (notification.link && onNavigateToLink) {
      onNavigateToLink(notification.link);
    }
  };
  const hasLink = !!notification.link && !!onNavigateToLink;

  return (
    <div
      className={`bg-card px-4 py-4 ${!notification.isRead ? "bg-primary/5" : ""} ${hasLink ? "cursor-pointer hover:bg-muted/50 transition-colors" : ""}`}
      onClick={hasLink ? handleRowClick : undefined}
      onKeyDown={hasLink ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleRowClick(); } } : undefined}
      role={hasLink ? "button" : undefined}
      tabIndex={hasLink ? 0 : undefined}
    >
      <div className="flex items-start gap-4">
        <div className={`w-10 h-10 ${iconColor} rounded-full flex items-center justify-center flex-shrink-0`}>
          <Bell className="w-5 h-5 text-white" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className={`text-sm ${!notification.isRead ? "font-semibold" : "font-medium"}`}>
              {notification.title}
            </h3>
            {!notification.isRead && (
              <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1.5" />
            )}
          </div>
          <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
            {notification.message}
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs text-muted-foreground">
              {notification.timestamp}
            </span>
            {hasLink && (
              <span className="text-xs text-primary font-medium">
                {notification.apiType === "swap_request_checkout"
                  ? "Concluir pagamento"
                  : "Clique para ver"}
              </span>
            )}
            {!notification.isRead && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onMarkAsRead(notification.id); }}
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                <Check className="w-3 h-3" />
                Marcar como lida
              </button>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(notification.id); }}
          className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors flex-shrink-0"
          aria-label="Excluir notificação"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
